import React, { memo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../common/components/form-field-wrapper/CommonFormFieldWrapper'
import styles from './SignUpProviderConfirmation.module.scss'
import CommonPhoneInput from '../../../common/components/phone-input/CommonPhoneInput'
import FormWrapper from '../../../common/components/form-wrapper/FormWrapper'
import Header from '../../../layouts/public-layout-form/components/Header'
import { useConfirmProviderRegistrationMutation } from '../../../service/user-service'
import { useGetCountriesQuery } from '../../../service/utility-service'
import { Country } from '../../../model/bill-settings/bill-settings'

type Props = {
}

type BusinessInformation = {
    code: string
    firstName: string
    lastName: string
    businessName: string
    password: string
    confirmPassword: string
    phoneNumber: string
    country: string;
}

export const CountrySelectionStep = ({ 
    countries, 
    onCountrySelect, 
    isLoading 
}: { 
    countries: Array<Country> | undefined, 
    onCountrySelect: (country: string) => void, 
    isLoading: boolean 
}) => {
    const [selectedCountry, setSelectedCountry] = useState<string>("")

    const handleContinue = () => {
        if (selectedCountry) {
            onCountrySelect(selectedCountry)
        }
    }

    return (
        <div className={styles.countrySelectionContainer}>
            <div className={styles.countrySelectionForm}>
                <h2 className={styles.countrySelectionTitle}>Select Country</h2>
                
                <div className={styles.selectWrapper}>
                    <select 
                        value={selectedCountry} 
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className={styles.countrySelect}
                    >
                        <option value="" disabled>Choose your country</option>
                        {countries?.map(country => (
                            <option key={country.id} value={country.isoCode}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                </div>
                
                <button 
                    onClick={handleContinue}
                    disabled={!selectedCountry || isLoading}
                    className={styles.continueButton}
                >
                    Continue
                </button>
            </div>
        </div>
    )
}

const SignUpProviderConfirmation: React.FC<Props> = memo((props: Props) => {
    const { code } = useParams();
    const navigate = useNavigate();
    const [confirmRegistration, { isLoading }] = useConfirmProviderRegistrationMutation();
    const [showMainForm, setShowMainForm] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<string>("");
    const { data: countries, isLoading: countriesLoading } = useGetCountriesQuery();
    

    const {
        register,
        getValues,
        watch,
        setValue,
        formState: { errors, isValid },
    } = useForm<BusinessInformation>({
        defaultValues: {
            code: code,
            firstName: '',
            lastName: '',
            businessName: '',
            phoneNumber: '',
            country: '',
        },
        reValidateMode: 'onSubmit',
        mode: 'onTouched'
    })

    const handleCountrySelect = (country: string) => {
        setSelectedCountry(country);
        setValue('country', country);
        setShowMainForm(true);
    }

    const submit = async () => {
        if (!isValid) return;

        try {
            const values = getValues();
            await confirmRegistration({
                ...values,
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                businessName: values.businessName.trim(),
            }).unwrap();
            toast.success('Successfully registered');
            navigate('/login');
        } catch (error: any) {
            toast.error(error.data?.title ?? 'Failed to confirm registration');
        }
    };

    const validatePassword = (value: string) => {

        // Check if password is at least 10 characters long
        if (value.length === 0) {
          return "Password is required";
        }
    
        // Check if password is at least 10 characters long
        if (value.length < 10) {
          return "Password must be at least 10 characters long";
        }
    
        // Check if password contains at least one uppercase letter
        if (!/[A-Z]/.test(value)) {
          return "Password must contain at least one uppercase letter";
        }
    
        // Check if password contains at least one lowercase letter
        if (!/[a-z]/.test(value)) {
          return "Password must contain at least one lowercase letter";
        }
    
        // Check if password contains at least one number
        if (!/\d/.test(value)) {
          return "Password must contain at least one number";
        }
    
        // Check if password contains at least one special character
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value)) {
          return "Password must contain at least one special character";
        }

        return true;
    }

    return (
        <div className={styles.container}>
            <Header />
            <div className={styles.contentContainer}>
            {!showMainForm ? (
                    <CountrySelectionStep 
                        countries={countries} 
                        onCountrySelect={handleCountrySelect} 
                        isLoading={countriesLoading}
                    />
                ) : (
                <div className={styles.pageContainer}>
                    <FormWrapper titleSection={{ title: 'Basic Information', subtitle: 'Tell us about your business.', buttonTitle: 'Proceed'}} onSaveAndContinue={submit} isNotValid={!isValid || isLoading } >
                        <div className={styles.container}>
                            <div className={styles.formContainer}>
                                <div className={`formFieldInlineContainer ${styles.formInlineRow}`}>
                                    <CommonFormFieldWrapper title="First name" required={true} errors={getFormInputErrors(errors.firstName?.message)}>
                                        <input {...register('firstName', { required: 'First name is required' })} placeholder="Enter First Name" />
                                    </CommonFormFieldWrapper>
                                    <CommonFormFieldWrapper title="Last name" required={true} errors={getFormInputErrors(errors.lastName?.message)}>
                                        <input {...register('lastName', { required: 'Last name is required' })} placeholder="Enter Last Name" />
                                    </CommonFormFieldWrapper>
                                </div>
                                <span className='spacer' />
                                <CommonFormFieldWrapper title="Business Name" required={true} errors={getFormInputErrors(errors.businessName?.message)}>
                                    <input {...register('businessName', { required: 'Business Name is required' })} placeholder="Enter Business Name" />
                                </CommonFormFieldWrapper>
                                <span className='spacer' />
                                <CommonFormFieldWrapper title="Password" required={true} errors={getFormInputErrors(errors.password?.message)}>
                                    <input type='password' {...register('password', { validate: validatePassword})} placeholder="Enter Password" />
                                </CommonFormFieldWrapper>
                                <span className='spacer' />
                                <CommonFormFieldWrapper title="Confirm Password" required={true} errors={getFormInputErrors(errors.confirmPassword?.message)}>
                                    <input type='password' {...register('confirmPassword', { required: 'Password must be confirmed' , validate: value => value === watch('password') || "Passwords do not match" })} placeholder="Confirm Password" />
                                </CommonFormFieldWrapper>
                                <span className='spacer' />
                                <CommonFormFieldWrapper title="Phone number" required={true} errors={getFormInputErrors(errors.phoneNumber?.message)}>
                                    <CommonPhoneInput
                                    registerInputProps={register('phoneNumber', {required: 'Phone Number is required' , pattern: {
                                        value: /^\+(?:[0-9] ?){6,}[0-9]$/,
                                        message: 'Invalid phone number'
                                    }})} 
                                    value={watch('phoneNumber') ?? ''} 
                                    defaultCountry='ng' 
                                    onChange={(value) => setValue('phoneNumber', value, {
                                        shouldDirty: true
                                    })} />
                                </CommonFormFieldWrapper>
                            </div>
                        </div>
                    </FormWrapper>
                </div>)}
            </div>
        </div>
    )
})

export default SignUpProviderConfirmation
